import { UserAgreementDialogComponent } from '../components/user/sections/user-agreements-data/user-agreement-dialog/user-agreement-dialog.component';
import { BaseComponentService } from './_app/base-component.service';
import { BehaviorSubject, catchError, map, throwError } from 'rxjs';
import { ApiPagination } from '../models/api/api-pagination.model';
import { Agreement } from '../models/management/agreement.model';
import { ApiResponse } from '../models/api/api-response.model';
import { ApiError } from '../models/api/api-error.model';
import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { API } from '../constants/api';

@Injectable({ providedIn: 'root' })
export class AgreementService {
  private _baseComponentService = inject(BaseComponentService);
  private _http: HttpClient = inject(HttpClient);

  types$ = new BehaviorSubject<string[]>([]);

  constructor() {
    if (this.types$.getValue().length === 0) {
      this.getAgreementTypes().subscribe();
    }
  }

  getAgreementTypes() {
    return this._http
      .get<ApiResponse<string[]>>(API.agreementTypesConfigs(), {
        withCredentials: true,
      })
      .pipe(
        catchError((error) => throwError(() => new ApiError(error))),
        map((response) => {
          this.types$.next(response.data ?? []);
          return response;
        })
      );
  }

  getAgreementsListing(params: any) {
    return this._http
      .get<ApiResponse<Agreement[]>>(API.agreementListing(), {
        withCredentials: true,
        params,
      })
      .pipe(
        catchError((error) => throwError(() => new ApiError(error))),
        map((response) => {
          response.pagination = new ApiPagination(response.pagination);
          response.data = (response.data ?? []).map(
            (json) => new Agreement(json)
          );
          return response;
        })
      );
  }

  getAgreementDetails(id?: string) {
    return this._http
      .get<ApiResponse<Agreement>>(API.agreementDetails(id), {
        withCredentials: true,
      })
      .pipe(
        catchError((error) => throwError(() => new ApiError(error))),
        map((response) => {
          response.data = new Agreement(response.data);
          return response;
        })
      );
  }

  postAgreement(data: any) {
    return this._http
      .post<ApiResponse<Agreement>>(API.agreement(), data, {
        withCredentials: true,
      })
      .pipe(
        catchError((error) => throwError(() => new ApiError(error))),
        map((response) => {
          response.data = new Agreement(response.data);
          return response;
        })
      );
  }

  patchAgreement(id: string, data: any) {
    return this._http
      .patch<ApiResponse<Agreement>>(API.agreementDetails(id), data, {
        withCredentials: true,
      })
      .pipe(
        catchError((error) => throwError(() => new ApiError(error))),
        map((response) => {
          response.data = new Agreement(response.data);
          return response;
        })
      );
  }

  deleteAgreementAttachment(pt?: Agreement) {
    return this._http
      .delete<ApiResponse<any>>(
        API.agreementAttachment(pt?.id, pt?.attachment),
        { withCredentials: true }
      )
      .pipe(
        catchError((error) => throwError(() => new ApiError(error))),
        map((response) => {
          return response;
        })
      );
  }

  deleteAgreement(id?: string) {
    return this._http
      .delete<ApiResponse<any>>(API.agreementDetails(id), {
        withCredentials: true,
      })
      .pipe(
        catchError((error) => throwError(() => new ApiError(error))),
        map((response) => {
          return response;
        })
      );
  }

  // Dialog
  openAgreementDialog(data?: any) {
    const dialogSize = '700px';
    return this._baseComponentService.dialog.open(
      UserAgreementDialogComponent,
      {
        maxWidth: dialogSize,
        minWidth: dialogSize,
        restoreFocus: false,
        autoFocus: false,
        data,
      }
    );
  }
}
